import type { MeteringGrant } from "@/client";

/**
 * Which kind of grant a row is. Derived from `kind`, which metering builds by slugifying
 * the grant's name in the billing provider — so it is not a fixed enum and can change
 * without a code change here. Every predicate falls through to "other" so a renamed grant
 * still renders somewhere instead of silently disappearing.
 */
export type GrantGroup = "onboarding" | "topup" | "allowance" | "other";

/** Which balance a grant draws from, after recovering rows metering could not classify. */
export type ResourceBucket = "vius" | "credits" | "other";

export const groupOf = (grant: MeteringGrant): GrantGroup => {
	const kind = (grant.kind ?? "").toLowerCase();
	// Checked first: an onboarding grant may also be named "top-up" or "allowance".
	if (kind.includes("onboarding")) return "onboarding";
	if (kind.includes("top_up") || kind.includes("topup")) return "topup";
	if (kind.includes("allowance")) return "allowance";
	return "other";
};

/**
 * Metering reports `resource: "unknown"` when the billing provider's credit-type name did
 * not match its configured ids — in live data that includes VIU top-ups whose credit type
 * is "VIU" rather than "Video Intelligence Units". Recover from the free text so those
 * grants still appear under the right heading. They remain absent from `totals`, which is
 * why a section's own subtotal can exceed the overview.
 */
export const resourceOf = (grant: MeteringGrant): ResourceBucket => {
	if (grant.resource === "vius" || grant.resource === "credits") {
		return grant.resource;
	}

	const text = `${grant.credit_type ?? ""} ${grant.label ?? ""}`.toLowerCase();
	if (text.includes("viu") || text.includes("video intelligence"))
		return "vius";
	if (text.includes("credit")) return "credits";
	return "other";
};

export type GrantSectionId =
	| "viu_allowance"
	| "credit_allowance"
	| "viu_topup"
	| "credit_topup"
	| "onboarding"
	| "other";

/**
 * Partition the grants into the page's sections. Metering already returns them in the
 * order we want (vius before credits, active before upcoming, oldest first), so this
 * filters without re-sorting. Every grant lands in exactly one section.
 */
export const sectionOf = (grant: MeteringGrant): GrantSectionId => {
	const group = groupOf(grant);
	const resource = resourceOf(grant);

	if (group === "onboarding") return "onboarding";
	if (group === "topup" && resource === "vius") return "viu_topup";
	if (group === "topup" && resource === "credits") return "credit_topup";
	if (group === "allowance" && resource === "vius") return "viu_allowance";
	if (group === "allowance" && resource === "credits")
		return "credit_allowance";
	return "other";
};

export const groupGrants = (
	grants: MeteringGrant[],
): Record<GrantSectionId, MeteringGrant[]> => {
	const sections: Record<GrantSectionId, MeteringGrant[]> = {
		viu_allowance: [],
		credit_allowance: [],
		viu_topup: [],
		credit_topup: [],
		onboarding: [],
		other: [],
	};

	for (const grant of grants) {
		sections[sectionOf(grant)].push(grant);
	}

	return sections;
};

/**
 * Allowances are only worth showing while they are spendable — next period's allowance is
 * not a balance the account holds yet.
 */
export const spendableNow = (grants: MeteringGrant[]): MeteringGrant[] =>
	grants.filter((grant) => grant.status === "active");

/**
 * A top-up drops off the list once it is fully drawn down — the balance it contributed is
 * already reflected in what has been used.
 */
export const withRemaining = (grants: MeteringGrant[]): MeteringGrant[] =>
	grants.filter((grant) => (grant.remaining ?? 0) > 0);

/** Sum a field across the rows actually shown in a section. */
export const sumOf = (
	grants: MeteringGrant[],
	field: "granted" | "consumed" | "remaining",
): number => grants.reduce((total, grant) => total + (grant[field] ?? 0), 0);
